import { DateTime } from 'luxon';
import { create } from 'zustand';

import type { ServerError } from 'models';
import type { OrderVo } from 'models/order';
import type { UserOrderEntry } from 'models/userOrder';
import {
    isUserOrderPending,
    selectRestingUserOrdersInMarket,
    userOrderEntryFromRestVo,
    userOrderEntryFromWsDto,
} from 'models/userOrder';
import type { UserOrderDTO, WsMessage, WsResponse, WsUnsubscribeCallback } from 'models/ws';
import { useBannerStore } from 'stores/zustand/bannerStore';
import { resolveEventTickerForMarketId } from 'stores/zustand/marketEntitiesStore';
import { getSubaccountNumber, useSubAccountStore } from 'stores/zustand/subAccountStore';
import { tradingContextSelectors, useTradingContextStore } from 'stores/zustand/tradingContextStore';
import { getOrders } from 'transport/domains/orders';
import { legacyExchangeWsClient } from 'transport/wsClients';
import logProviderFactory from 'utils/logger/logProviderFactory';
import { noop } from 'utils/noop';

import { useAuthStore } from './authStore';

/**
 * Live cache of the user's orders, keyed by order id. Hydrated by the
 * event/market-scoped REST fetches (`refreshOrdersForEvent` below, the
 * resting-orders modal, the portfolio all-resting-orders load), kept fresh by
 * the `user_orders` WS channel (via this module's own buffered drain, which
 * owns trading-context gating), and trimmed by cancel confirmations and
 * reconciliation refetches.
 *
 * Order history one-shots (order history modal, activity CSV export) do NOT
 * write here — they are plain React Query fetches. Only order data that must
 * update live belongs in this store.
 */
export interface UserOrdersState {
    orders: ReadonlyMap<string, UserOrderEntry>;
    /**
     * True once the portfolio's load-everything flow has fetched every resting
     * order AND loaded the backing markets/events for display. Reset alongside
     * `orders` so a subaccount switch or logout re-triggers the full load.
     */
    loadedAllRestingOrders: boolean;
    actions: UserOrdersActions;
}

export interface UserOrdersActions {
    /** Upsert a batch of REST rows. Identity-preserves unchanged entries. */
    hydrateFromRest: (vos: OrderVo[]) => void;
    /** Apply a drained batch of `user_orders` WS messages (already gated/deduped by the caller). */
    applyWsOrders: (dtos: UserOrderDTO[]) => void;
    /** Drop a single order (confirmed cancel). */
    removeOrder: (orderId: string) => void;
    /**
     * Applied after a reconciliation refetch pages through an event's CURRENT
     * resting set: an order that was cached as resting when the refetch
     * started (`snapshot`), came back in none of the pages, and hasn't been
     * touched since the snapshot must have left the resting set
     * (filled/canceled) while unwatched. Its final status isn't knowable from
     * a resting-only response, so the stale row is dropped rather than given
     * a fabricated status — matching how a confirmed cancel removes its row.
     * An order whose `lastUpdateTs` moved since the snapshot received fresher
     * data (a WS message, or another REST write) while the refetch was in
     * flight; trust that instead. An order that turned `pending` since the
     * snapshot (a fresh optimistic row) isn't in the resting set a
     * resting-only refetch enumerates, so it's kept too.
     */
    dropRestingOrdersMissingFromRefetch: (
        snapshot: ReadonlyMap<string, string>,
        fetchedOrderIds: ReadonlySet<string>,
    ) => void;
    allRestingOrdersLoaded: () => void;
    reset: () => void;
}

function userOrderEntriesEqual(a: UserOrderEntry, b: UserOrderEntry): boolean {
    return (
        a.id === b.id &&
        a.userId === b.userId &&
        a.marketId === b.marketId &&
        a.marketTicker === b.marketTicker &&
        a.isYes === b.isYes &&
        a.isSell === b.isSell &&
        a.priceDollars === b.priceDollars &&
        a.createTs === b.createTs &&
        a.lastUpdateTs === b.lastUpdateTs &&
        a.expirationTs === b.expirationTs &&
        a.status === b.status &&
        a.orderAction === b.orderAction &&
        a.orderType === b.orderType &&
        a.fillCount === b.fillCount &&
        a.remainingCount === b.remainingCount &&
        a.initialCount === b.initialCount &&
        a.takerFillCost === b.takerFillCost &&
        a.makerFillCost === b.makerFillCost &&
        a.takerFillFees === b.takerFillFees &&
        a.makerFillFees === b.makerFillFees
    );
}

export const useUserOrdersStore = create<UserOrdersState>()((set, get) => ({
    orders: new Map<string, UserOrderEntry>(),
    loadedAllRestingOrders: false,
    actions: {
        hydrateFromRest: (vos) => {
            if (vos.length === 0) return;
            const current = get().orders;
            let next: Map<string, UserOrderEntry> | null = null;
            for (const vo of vos) {
                const existing = (next ?? current).get(vo.order_id);
                const entry = userOrderEntryFromRestVo(vo, existing);
                if (existing && userOrderEntriesEqual(existing, entry)) continue;
                next = next ?? new Map(current);
                next.set(entry.id, entry);
            }
            if (next) set({ orders: next });
        },
        applyWsOrders: (dtos) => {
            if (dtos.length === 0) return;
            const current = get().orders;
            let next: Map<string, UserOrderEntry> | null = null;
            for (const dto of dtos) {
                const existing = (next ?? current).get(dto.order_id);
                const entry = userOrderEntryFromWsDto(dto, existing);
                if (existing && userOrderEntriesEqual(existing, entry)) continue;
                next = next ?? new Map(current);
                next.set(entry.id, entry);
            }
            if (next) set({ orders: next });
        },
        removeOrder: (orderId) => {
            const current = get().orders;
            if (!current.has(orderId)) return;
            const next = new Map(current);
            next.delete(orderId);
            set({ orders: next });
        },
        dropRestingOrdersMissingFromRefetch: (snapshot, fetchedOrderIds) => {
            const current = get().orders;
            let next: Map<string, UserOrderEntry> | null = null;
            snapshot.forEach((lastUpdateTsAtSnapshot, orderId) => {
                if (fetchedOrderIds.has(orderId)) return;
                const order = (next ?? current).get(orderId);
                if (!order || order.remainingCount <= 0 || isUserOrderPending(order)) return;
                if (order.lastUpdateTs !== lastUpdateTsAtSnapshot) return;
                next = next ?? new Map(current);
                next.delete(orderId);
            });
            if (next) set({ orders: next });
        },
        allRestingOrdersLoaded: () => set({ loadedAllRestingOrders: true }),
        reset: () => set({ orders: new Map<string, UserOrderEntry>(), loadedAllRestingOrders: false }),
    },
}));

/**
 * True when this event already has at least one order cached, regardless of
 * how it got here — a market-scoped load (e.g. the resting-orders modal)
 * writes into this same shared cache without going through
 * `refreshOrdersForEvent`, so a caller deciding whether an event's FIRST
 * fetch this session can skip reconciliation must check this, not just its
 * own fetch-tracking state.
 *
 * `resolveEventTicker` re-resolves each order's event at check time rather
 * than trusting anything stored on the entry — market data can be registered
 * (or scoped stores swapped) after the order was cached, so only a fresh
 * lookup is trustworthy. Callers pass the same market-store-backed resolver
 * (`resolveEventTickerForMarketId`) used elsewhere for order/event resolution.
 */
export function hasCachedOrdersForEvent(
    orders: ReadonlyMap<string, UserOrderEntry>,
    eventTicker: string,
    resolveEventTicker: (marketId: string) => string,
): boolean {
    for (const order of orders.values()) {
        if (resolveEventTicker(order.marketId) === eventTicker) return true;
    }
    return false;
}

/**
 * Snapshot (order id → `lastUpdateTs`) of the event's cached orders that
 * currently look resting, taken by `refreshOrdersForEvent` below right
 * before it issues a reconciliation refetch. Pending orders (optimistic rows
 * whose status is still settling) have `remainingCount > 0` but aren't in the
 * backend's resting set yet, so a resting-only refetch won't return them —
 * excluding them here keeps `dropRestingOrdersMissingFromRefetch` from
 * deleting a live pending order.
 */
export function snapshotRestingOrderUpdateTimesForEvent(
    orders: ReadonlyMap<string, UserOrderEntry>,
    eventTicker: string,
    resolveEventTicker: (marketId: string) => string,
): Map<string, string> {
    const snapshot = new Map<string, string>();
    orders.forEach((order, orderId) => {
        if (order.remainingCount <= 0 || isUserOrderPending(order)) return;
        if (resolveEventTicker(order.marketId) !== eventTicker) return;
        snapshot.set(orderId, order.lastUpdateTs);
    });
    return snapshot;
}

// ---------------------------------------------------------------------------
// WS order pump + refreshOrdersForEvent (formerly UserStore's WS message
// buffering and event-scoped resting-orders refetch). Plain module-scope
// state, not React state — nothing renders off it directly; consumers read
// the effects of it through `useUserOrdersStore` above.
// ---------------------------------------------------------------------------

const ORDERS_REFRESH_ERROR_LOG_DEDUPE_MS = 5 * 60 * 1000;
const ORDERS_REFRESH_ERROR_LOG_MAX_CONTEXTS = 200;

let wsUserOrderMessagesBuffer: Record<string, UserOrderDTO> = {};
let lastUserOrderWsProcessedTs: DateTime | undefined = undefined;
let unsubscribeOrdersWs: WsUnsubscribeCallback | null = null;
const inFlightOrdersContexts = new Map<string, number>();
const lastOrdersRefreshErrorLogs = new Map<string, { errorKey: string; loggedAt: number }>();

function getOrdersContextKeyForEvent(eventTicker: string): string {
    return `orders:event:${eventTicker}`;
}

function startOrdersRefresh(contextKey: string): void {
    const current = inFlightOrdersContexts.get(contextKey) ?? 0;
    inFlightOrdersContexts.set(contextKey, current + 1);
}

function finishOrdersRefresh(contextKey: string): void {
    const current = inFlightOrdersContexts.get(contextKey);
    if (!current || current <= 1) {
        inFlightOrdersContexts.delete(contextKey);
        return;
    }
    inFlightOrdersContexts.set(contextKey, current - 1);
}

// Note: the WS drain path (below) calls this with a market id, not an event
// ticker, same as the original UserStore implementation — `contextKey` ends
// up namespaced `orders:event:${marketId}` there, distinct from the
// `refreshOrdersForEvent`-populated keys. Preserved verbatim; not fixed here.
function isOrdersContextInFlight(eventTicker: string): boolean {
    return inFlightOrdersContexts.has(getOrdersContextKeyForEvent(eventTicker));
}

function shouldSuppressOrdersRefreshError(error: ServerError): boolean {
    return (
        error.cancelled ||
        error.notFound ||
        error.timeout ||
        error.tooManyRequests ||
        error.unauthorized ||
        error.forbidden
    );
}

function getOrdersRefreshErrorKey(error: ServerError): string {
    const category =
        [
            error.unknown && 'unknown',
            error.unavailable && 'unavailable',
            error.internal && 'internal',
            error.invalid && 'invalid',
            error.conflict && 'conflict',
            error.maintenance && 'maintenance',
        ]
            .filter(Boolean)
            .join(',') || 'unclassified';
    const code = error.error?.code || 'no_code';
    const message = error.error?.message || error.error?.details || 'no_message';
    return `category=${category}; code=${code}; message=${message}`;
}

function markOrdersRefreshFailed(contextKey: string, error: ServerError): void {
    const errorKey = getOrdersRefreshErrorKey(error);
    if (shouldSuppressOrdersRefreshError(error)) {
        return;
    }

    const now = Date.now();
    const previous = lastOrdersRefreshErrorLogs.get(contextKey);
    if (previous?.errorKey === errorKey && now - previous.loggedAt < ORDERS_REFRESH_ERROR_LOG_DEDUPE_MS) {
        return;
    }

    if (lastOrdersRefreshErrorLogs.size >= ORDERS_REFRESH_ERROR_LOG_MAX_CONTEXTS) {
        const oldest = lastOrdersRefreshErrorLogs.keys().next().value;
        if (oldest !== undefined) {
            lastOrdersRefreshErrorLogs.delete(oldest);
        }
    }
    lastOrdersRefreshErrorLogs.set(contextKey, { errorKey, loggedAt: now });
    logProviderFactory.logError(`Error fetching orders: ${errorKey}`);
}

// Fetches one page of the event's CURRENT resting orders and writes it into
// the shared orders cache. `onSuc`'s `fetchedOrderIds` reports every order id
// the page returned even when a stale response skips the cache write — the
// caller's reconciliation (`dropRestingOrdersMissingFromRefetch`) applies its
// own staleness guard at completion time. `isStale` is re-checked immediately
// before writing the response into the shared orders cache (not just at the
// caller's completion handler) — a logout/subaccount-switch/stale-reconnect
// can invalidate this request while it's in flight, and applying its response
// after that point would repopulate the cache with data that no longer
// belongs to the current session/subaccount.
function loadRestingOrdersPageForEvent(
    eventTicker: string,
    pageSize: number,
    cursor: string | undefined,
    onSuc: (fetchedOrderIds: Array<string>, couldHaveMore: boolean, cursor?: string) => void,
    onErr: (err: ServerError) => void,
    onLog?: (parsedError: ServerError) => void,
    isStale?: () => boolean,
): void {
    const { userId, isLoggedInMember } = useAuthStore.getState();
    if (!eventTicker || !(userId ?? '') || !isLoggedInMember) {
        return;
    }
    getOrders(
        userId ?? '',
        (data, newCursor) => {
            if (!isStale?.()) {
                useUserOrdersStore.getState().actions.hydrateFromRest(data);
                useBannerStore.getState().actions.queryExchangeRecovered();
            }
            onSuc(
                data.map((vo) => vo.order_id),
                !!newCursor,
                newCursor,
            );
        },
        onErr,
        noop,
        {
            event_ticker: eventTicker,
            page_size: pageSize,
            cursor: cursor,
            status: 'resting',
        },
        onLog,
    );
}

/**
 * Refetches an event's resting orders and writes them into the shared cache.
 * `onSettled` lets callers (namely `tradingSync`'s "fetch resting orders once
 * per session" tracking) know whether the fetch actually completed, so a
 * failed attempt — or one skipped because another was already in flight —
 * doesn't get treated as done and permanently skipped thereafter.
 *
 * `reconcile` corrects locally-cached orders that may have left the resting
 * set (filled/canceled) while unwatched — during a WS gap, or while the
 * event's context was inactive. A resting-only response can only ever
 * ADD/update rows, so the refetch also snapshots the event's cached resting
 * orders up front and, once the last page lands, drops any cached row the
 * authoritative resting response no longer returned. This keeps every
 * request scoped to `status=resting` — re-downloading the event's entire
 * order history (executed/canceled included) just to detect removals would
 * make query-exchange scan far more rows than the handful of orders a single
 * user has resting on one event. A true first-ever fetch for an event has no
 * cached rows to reconcile, so it skips the snapshot entirely.
 *
 * `resolveEventTicker` resolves an order's parent event via the caller's
 * market-store lookup — the same one `tradingSync.ts` already threads through
 * `hasCachedOrdersForEvent` (`resolveEventTickerForMarketId` from
 * `marketEntitiesStore`, passed in by the caller rather than imported directly here).
 */
export function refreshOrdersForEvent(
    eventTicker: string,
    resolveEventTicker: (marketId: string) => string,
    options: {
        reconcile?: boolean;
        onSettled?: (succeeded: boolean) => void;
        isStale?: () => boolean;
    } = {},
): void {
    const onSettled = options.onSettled ?? noop;
    const isLoggedInMember = useAuthStore.getState().isLoggedInMember;
    if (!isLoggedInMember || !eventTicker) {
        onSettled(false);
        return;
    }
    const contextKey = getOrdersContextKeyForEvent(eventTicker);
    if (inFlightOrdersContexts.has(contextKey)) {
        onSettled(false);
        return;
    }
    startOrdersRefresh(contextKey);

    // A single user's resting orders on one event are almost always a
    // handful of rows, and the backend runs a `LIMIT page_size + 1` scan per
    // page — so a huge page size is pure cost. The cursor loop below already
    // follows pages for the rare user with more.
    const PAGE_SIZE = 200;
    const restingSnapshot = options.reconcile
        ? snapshotRestingOrderUpdateTimesForEvent(useUserOrdersStore.getState().orders, eventTicker, resolveEventTicker)
        : null;
    const fetchedOrderIds = new Set<string>();

    const onRefreshError = (): void => {
        finishOrdersRefresh(contextKey);
        onSettled(false);
    };

    const loadPage = (cursor?: string): void => {
        loadRestingOrdersPageForEvent(
            eventTicker,
            PAGE_SIZE,
            cursor,
            (pageOrderIds, couldHaveMore, nextCursor) => {
                pageOrderIds.forEach((orderId) => fetchedOrderIds.add(orderId));
                if (couldHaveMore && nextCursor !== cursor) {
                    loadPage(nextCursor);
                } else {
                    // Only drop stale rows off a cleanly-finished pagination
                    // (`!couldHaveMore`) — bailing on a stuck cursor means
                    // later pages were never seen, and a fetch flagged stale
                    // mid-flight must not touch the cache at all (same guard
                    // `loadRestingOrdersPageForEvent` applies to its own
                    // writes).
                    if (!couldHaveMore && restingSnapshot?.size && !options.isStale?.()) {
                        useUserOrdersStore
                            .getState()
                            .actions.dropRestingOrdersMissingFromRefetch(restingSnapshot, fetchedOrderIds);
                    }
                    finishOrdersRefresh(contextKey);
                    onSettled(true);
                }
            },
            onRefreshError,
            (error) => markOrdersRefreshFailed(contextKey, error),
            options.isStale,
        );
    };

    loadPage();
}

function queueWsUserOrderMessages(response: WsResponse): void {
    const messages = typeof response === 'object' ? [response as WsMessage] : (response as WsMessage[]);

    const activeSubaccount = getSubaccountNumber();
    for (const msg of messages) {
        if (msg.type === 'user_order') {
            const payload = msg.msg as UserOrderDTO;
            if ((payload.subaccount ?? 0) !== activeSubaccount) continue;
            wsUserOrderMessagesBuffer[payload.order_id] = payload;
        }
    }

    // Process messages buffer if it's been a while since last processing
    const neverProcessed = !lastUserOrderWsProcessedTs;
    const processedTooLongAgo =
        !!lastUserOrderWsProcessedTs && DateTime.now().diff(lastUserOrderWsProcessedTs).as('seconds') > 4;
    const smallBatch =
        Object.keys(wsUserOrderMessagesBuffer).length <= 2 &&
        !!lastUserOrderWsProcessedTs &&
        DateTime.now().diff(lastUserOrderWsProcessedTs).as('milliseconds') > 1000;
    if (neverProcessed || processedTooLongAgo || smallBatch) {
        processWsUserOrderMessagesBuffer();
    }
}

function processWsUserOrderMessagesBuffer(): void {
    // If buffer is empty, nothing to process
    if (Object.keys(wsUserOrderMessagesBuffer).length === 0) {
        return;
    }

    // Get all buffered messages
    const messages = Object.values(wsUserOrderMessagesBuffer);

    // Clear the buffer
    wsUserOrderMessagesBuffer = {};

    // Re-check the active subaccount at drain time. The enqueue path
    // (`queueWsUserOrderMessages`) already drops mismatched messages, but a
    // subaccount switch can happen *between* enqueue and drain — the queued
    // flush runs on a 1s/4s cadence — so anything buffered for the
    // previously-active subaccount must be discarded now to avoid
    // resurrecting stale orders post-switch.
    const activeSubaccount = getSubaccountNumber();
    const inScopeMessages = messages.filter((msg) => (msg.subaccount ?? 0) === activeSubaccount);

    // Eliminate duplicates by order ID, keeping only the latest message per order
    const uniqueMessagesMap: Record<string, UserOrderDTO> = {};
    inScopeMessages.forEach((msg) => {
        uniqueMessagesMap[msg.order_id] = msg;
    });
    const uniqueMessages = Object.values(uniqueMessagesMap);

    // Process each unique message, batching the survivors into a single
    // store write so one drain triggers at most one subscriber pass.
    const messagesToApply: UserOrderDTO[] = [];
    uniqueMessages.forEach((msg) => {
        const eventTicker = resolveEventTickerForMarketId(msg.market_id) || undefined;

        const tradingContextState = useTradingContextStore.getState();
        const hasPortfolioContext = tradingContextSelectors.portfolioActive(tradingContextState);
        const hasOrderContext =
            !!eventTicker && tradingContextSelectors.hasOrderEventTicker(tradingContextState, eventTicker);

        let hasEventContext = false;
        if (!hasPortfolioContext && !hasOrderContext) {
            hasEventContext = !!eventTicker && tradingContextSelectors.hasEventTicker(tradingContextState, eventTicker);
        }

        const shouldProcess = hasPortfolioContext || hasOrderContext || hasEventContext;
        if (shouldProcess && !isOrdersContextInFlight(msg.market_id)) {
            messagesToApply.push(msg);
        }
    });
    useUserOrdersStore.getState().actions.applyWsOrders(messagesToApply);

    // Update last processed timestamp
    lastUserOrderWsProcessedTs = DateTime.now();
}

/** Subscribes to the `user_orders` WS channel; called once at session bootstrap. */
export function subscribeUserOrdersWs(): void {
    unsubscribeOrdersWs = legacyExchangeWsClient.subscribe({ channels: ['user_orders'] }, queueWsUserOrderMessages);
}

/** Unsubscribes from the `user_orders` WS channel; called from session teardown. */
export function unsubscribeUserOrdersWs(): void {
    unsubscribeOrdersWs && unsubscribeOrdersWs();
}

/** Clears in-flight/error-log bookkeeping on logout/forced-disconnect teardown. */
export function resetUserOrdersRefreshState(): void {
    inFlightOrdersContexts.clear();
    lastOrdersRefreshErrorLogs.clear();
}

/**
 * Test-only: resets the WS buffer/flush-timing state this module keeps for
 * the lifetime of the page session (production never resets it on logout
 * either — see `resetUserOrdersRefreshState`), so each test starts from a
 * clean "never processed" state instead of inheriting timing from whatever
 * test ran previously in the same file.
 */
export function resetUserOrdersWsInternalsForTesting(): void {
    wsUserOrderMessagesBuffer = {};
    lastUserOrderWsProcessedTs = undefined;
}

/** Resting orders in one market, newest first. */
export function useRestingOrdersInMarket(marketId: string): UserOrderEntry[] {
    const orders = useUserOrdersStore((s) => s.orders);
    return selectRestingUserOrdersInMarket(orders, marketId);
}

/**
 * Number of resting orders in one market. Primitive selector, so subscribers
 * (e.g. every market tile on an event page) only re-render when their
 * market's count actually changes.
 */
export function useNumRestingOrdersInMarket(marketId: string): number {
    return useUserOrdersStore((s) => {
        let count = 0;
        s.orders.forEach((order) => {
            if (order.remainingCount > 0 && order.marketId === marketId) count += 1;
        });
        return count;
    });
}

let authUnsubscribe: (() => void) | null = null;
let subaccountUnsubscribe: (() => void) | null = null;

function ensureResetSubscriptions(): void {
    if (!authUnsubscribe) {
        // The forced-disconnect teardown path also flips `isLoggedInMember`
        // (TransportLayer sets the zustand logout state), so this covers both
        // explicit logout and session-expiry — matching the old
        // `OrderStore.dispose()` wipe via the app logout teardown.
        authUnsubscribe = useAuthStore.subscribe((state, prev) => {
            if (state.isLoggedInMember === prev.isLoggedInMember || state.isLoggedInMember) return;
            useUserOrdersStore.getState().actions.reset();
        });
    }
    if (!subaccountUnsubscribe) {
        // Drop in-memory orders the moment the active subaccount changes —
        // they belong to the previous subaccount.
        subaccountUnsubscribe = useSubAccountStore.subscribe((state, prev) => {
            if (state.subaccountNumber === prev.subaccountNumber) return;
            useUserOrdersStore.getState().actions.reset();
        });
    }
}

if (typeof window !== 'undefined') {
    ensureResetSubscriptions();
}
